import {
  decrementCurrentContributionStep,
  decrementCurrentStep,
} from "@/store/wizard";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { WizardStepError } from "../../WizardStepError";
import { WizardStepSkeleton } from "../../WizardStepSkeleton";
import { BeneficiaryContributionAllocation } from "../components/BeneficiaryContributionAllocation";
import ContributionActions from "../components/ContributionActions";
import { useBeneficiaryAllocation } from "../hooks/useBeneficiaryAllocation";

interface BeneficiaryContributionStepProps {
  isUserEnrolled?: boolean;
  onNext: () => void;
}

const BeneficiaryContributionStep: React.FC<
  BeneficiaryContributionStepProps
> = ({ isUserEnrolled, onNext }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    beneficiaries,
    fundConfig,
    loading,
    error,
    isSubmitting,
    updatePersonalAllocation,
    updateEmployerAllocation,
    splitPersonalEqual,
    splitEmployerEqual,
    resetPersonal,
    resetEmployer,
    handleSubmitAllocations,
    initializeAllocationData,
  } = useBeneficiaryAllocation(isUserEnrolled);

  useEffect(() => {
    initializeAllocationData();
  }, [initializeAllocationData]);

  const handlePrevious = () => {
    if (isUserEnrolled) {
      dispatch(decrementCurrentStep());
    } else {
      dispatch(decrementCurrentContributionStep());
    }
  };

  const handleNext = async () => {
    try {
      const success = await handleSubmitAllocations();
      if (success) {
        onNext();
      }
    } catch {
      // Error is handled in the hook
    }
  };

  if (loading) return <WizardStepSkeleton />;

  if (error) {
    return <WizardStepError step={3} handlePrevious={handlePrevious} />;
  }

  return (
    <div className="space-y-8">
      <BeneficiaryContributionAllocation
        type="personal"
        toolTipText={t(
          "onboarding.contributions.allocation_tooltips.personal_contribution",
        )}
        label={fundConfig.personal.label}
        total={fundConfig.personal.total}
        beneficiaries={beneficiaries}
        onUpdate={updatePersonalAllocation}
        onSplit={splitPersonalEqual}
        onReset={resetPersonal}
      />

      {fundConfig.employer.total !== 0 && (
        <BeneficiaryContributionAllocation
          type="employer"
          toolTipText={t(
            "onboarding.contributions.allocation_tooltips.employer_contribution",
          )}
          label={fundConfig.employer.label}
          total={fundConfig.employer.total}
          beneficiaries={beneficiaries}
          onUpdate={updateEmployerAllocation}
          onSplit={splitEmployerEqual}
          onReset={resetEmployer}
        />
      )}

      <ContributionActions
        isUserEnrolled={isUserEnrolled}
        isSubmitting={isSubmitting}
        currentStep={1}
        onPrevious={handlePrevious}
        onNext={handleNext}
      />
    </div>
  );
};

export default BeneficiaryContributionStep;
